import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { GiftIcon } from 'lucide-react';
import { useCartActions, type CartState } from '../../contexts/StoreContext';
import { inputCls } from '../../ui/styles';
import { ease } from '../../core/format/motion';
type Props = {
  gifts: CartState['gifts'];
  compact?: boolean;
};

/**
 * Vrijgespeelde cadeaus als eigen regels in de mand, onder de producten.
 *
 * Bewust géén nepproducten in de mand: ze tellen niet mee in aantal of bedrag, gaan niet als
 * bestelregel naar de server (die past de cadeauregels zelf toe) en verdwijnen vanzelf als de
 * mand onder de grens zakt. Ze zien er wel uit als een regel, zodat de klant ziet wat er in de
 * doos komt, en hier kiest ze ook welk reisformaat.
 */
export function CartGifts({
  gifts,
  compact = false
}: Props) {
  const {
    chooseGift
  } = useCartActions();
  const reduce = useReducedMotion();
  if (gifts.length === 0) return null;
  return <ul aria-label="Cadeaus bij je bestelling" className="divide-y divide-line border-t border-line" data-id="element-6705" data-mp="2su3n6w8msbu8">
      <AnimatePresence initial={false} data-id="element-6706" data-mp="1b9biygn9mlqg">
        {gifts.map(({
        tier,
        choice
      }) => {
        const selectId = `gift-${tier.ruleId}`;
        return <motion.li key={tier.ruleId} initial={reduce ? false : {
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={reduce ? {
          opacity: 0
        } : {
          opacity: 0,
          transition: {
            duration: 0.15,
            ease
          }
        }} transition={{
          duration: 0.22,
          ease
        }} className={compact ? 'py-3' : 'py-5'} data-id="element-6707" data-mp="11d8e331148zt">
              <div className="flex gap-4" data-id="element-6708" data-mp="1kbkp6ubi3sy1">
                <span aria-hidden="true" className={`flex shrink-0 items-center justify-center bg-blush text-clay ${compact ? 'h-12 w-12' : 'h-24 w-20'}`} data-id="element-6709" data-mp="1kat4iejfzfmj">
                  <GiftIcon className="h-5 w-5" strokeWidth={1.5} data-id="element-6710" data-mp="2cyp0fh16t3i9" />
                </span>
                <div className="min-w-0 flex-1" data-id="element-6711" data-mp="2hrqg3r97x72w">
                  <div className="flex items-start justify-between gap-3" data-id="element-6712" data-mp="2rmypsv6t6bg6">
                    <div className="min-w-0" data-id="element-6713" data-mp="2rkupen84bukn">
                      <p className="text-[13px] text-clay" data-id="element-6714" data-mp="2mnm62jb8hkht">Cadeau van ons</p>
                      <h3 className={`mt-0.5 font-display leading-snug text-ink ${compact ? 'text-[15px]' : 'text-[1.05rem]'}`} data-id="element-6715" data-mp="2im926erpbrdj">{tier.title}</h3>
                    </div>
                    <span className="shrink-0 text-[14px] text-clay" data-id="element-6716" data-mp="2dmw9vei29s3k">Gratis</span>
                  </div>
                  {!compact && !tier.choices?.length && <p className="mt-1 text-[13px] leading-snug text-muted" data-id="element-6717" data-mp="23wupoxstwttx">{tier.description}</p>}
                  {choice !== null && tier.choices && <div className="mt-2" data-id="element-6718" data-mp="1yyji6tq0avev">
                      <label htmlFor={selectId} className="mb-1 block text-[13px] text-muted" data-id="element-6719" data-mp="1ux7wgza96uby">
                        Welke wil je?
                      </label>
                      {/* Native keuzelijst: op de telefoon de eigen kiezer van het toestel. */}
                      <select id={selectId} value={choice} onChange={e => chooseGift(tier.ruleId, e.target.value)} className={`${inputCls} py-2`} data-id="element-6720" data-mp="1l0ld6exksbc1">
                        {tier.choices.map(c => <option key={c} value={c} data-id="element-6721" data-mp="5hne4zbkhe98">
                            {c}
                          </option>)}
                      </select>
                    </div>}
                </div>
              </div>
            </motion.li>;
      })}
      </AnimatePresence>
    </ul>;
}